import { ArticleHeader } from '../components/ArticleHeader.js';
import { PostFooterCTA } from '../components/PostFooterCTA.js';
import { Prose } from '../components/Prose.js';
import { ShareRow } from '../components/ShareRow.js';
import { POSTS, getPost } from '../posts.js';

/**
 * The /blogs/<slug>/ detail page.
 *
 * Renders the MDX body inside `<Prose>` so post authors don't
 * need to import a typography wrapper per file; styles are
 * scoped to descendants of that container. Below the body sit
 * three fixed beats — share row, install/star CTA, prev/next
 * strip — in that order so the highest-intent reader (one who
 * finished the article) hits a copy/share affordance, then a
 * conversion ask, then a path back into the rest of the blog.
 *
 * An unknown slug renders a small "not found" block instead of
 * the SSR throwing. AI crawlers see a clean stub; humans see a
 * link back to the index without a generic 404 chrome.
 */
export function BlogPost({ slug }: { readonly slug: string }): JSX.Element {
  const post = getPost(slug);
  if (post === undefined) {
    return (
      <section className="border-b border-border">
        <div className="mx-auto w-full max-w-6xl px-4 py-24 md:px-8 md:py-32">
          <div className="mx-auto max-w-prose">
            <p className="font-mono text-xs uppercase tracking-widish text-muted">
              ~/memento/blogs
            </p>
            <h1 className="mt-4 text-3xl font-medium tracking-tight text-fg md:text-4xl">
              Post not found.
            </h1>
            <p className="mt-6 text-base text-fg/75">
              No post with the slug <code className="font-mono text-fg">{slug}</code>. It may have
              been renamed or unpublished.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3 font-mono text-sm">
              <a
                href="/blogs/"
                className="text-fg underline decoration-border underline-offset-4 hover:decoration-accent"
              >
                ← All posts
              </a>
              <span aria-hidden className="text-muted">
                ·
              </span>
              <a
                href="/"
                className="text-fg underline decoration-border underline-offset-4 hover:decoration-accent"
              >
                Memento home
              </a>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const PostBody = post.Component;
  const index = POSTS.findIndex((p) => p.slug === post.slug);
  // POSTS is sorted newest-first: a smaller index means a newer
  // post, a larger index means an older one. From the reader's
  // perspective the "newer" post is the natural "next", the
  // "older" is the "previous" — exposed under those words below.
  const newer = index > 0 ? POSTS[index - 1] : undefined;
  const older = index < POSTS.length - 1 ? POSTS[index + 1] : undefined;

  return (
    <section className="border-b border-border">
      <div className="mx-auto w-full max-w-6xl px-4 py-12 md:px-8 md:py-20">
        <ArticleHeader post={post} />
        <div className="mt-10">
          <Prose>
            <PostBody />
          </Prose>
        </div>
        <ShareRow title={post.title} slug={post.slug} />
        <PostFooterCTA />
        {newer !== undefined || older !== undefined ? (
          <nav
            aria-label="More posts"
            className="mx-auto mt-16 flex max-w-prose flex-col gap-6 border-t border-border pt-8 sm:flex-row sm:justify-between"
          >
            {older !== undefined ? (
              <a href={`/blogs/${older.slug}/`} className="group block max-w-xs">
                <p className="font-mono text-xs uppercase tracking-widish text-muted">← Older</p>
                <p className="mt-2 text-base font-medium text-fg group-hover:text-accent">
                  {older.title}
                </p>
              </a>
            ) : (
              <span aria-hidden />
            )}
            {newer !== undefined ? (
              <a href={`/blogs/${newer.slug}/`} className="group block max-w-xs sm:text-right">
                <p className="font-mono text-xs uppercase tracking-widish text-muted">Newer →</p>
                <p className="mt-2 text-base font-medium text-fg group-hover:text-accent">
                  {newer.title}
                </p>
              </a>
            ) : (
              <span aria-hidden />
            )}
          </nav>
        ) : null}
      </div>
    </section>
  );
}
